import {CommonModule} from '@angular/common';
import {HttpBackend, HttpClient, HttpErrorResponse} from '@angular/common/http';
import {ChangeDetectorRef, Component, HostListener, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {
  PrCertificationDetailDto,
  PrPageCommentCreateDto,
  PrPageCommentDto,
  PrPageDto
} from '../../../../../generated-model/models';
import {PrService} from '../pr/pr.service';
import {SearchIconComponent} from '../../../../shared/components/search-icon/search-icon.component';
import {StandardMarkDirective} from '../../../../shared/components/standard-mark/standard-mark.directive';

type ContactFieldKey = 'address' | 'tel' | 'email' | 'latitude' | 'longitude';

interface ContactInfoItem {
  key: ContactFieldKey;
  label: string;
  value: string;
  href: string | null;
  hidden: boolean;
  icon: string;
}

@Component({
  selector: 'app-pr-public-show',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, SearchIconComponent, StandardMarkDirective],
  templateUrl: './pr-public-show.html',
  styleUrl: './pr-public-show.scss'
})
export class PrPublicShowComponent implements OnInit {
  private static readonly PREVIEW_STORAGE_KEY = 'pr-page-preview';
  page?: PrPageDto;
  isLoading = true;
  notFound = false;
  isSubmittingComment = false;
  commentSaved = false;
  submitError = '';
  selectedAlbumImage: string | null = null;
  searchQuery = '';
  private currentRunpage = '';
  private readonly publicHttp: HttpClient;
  @ViewChild('contactModalTpl') private contactModalTpl!: TemplateRef<unknown>;
  expandedCertificationId: number | null = null;
  expandAllCertifications = false;
  certificationViewMode: 'grid' | 'row' = 'row';
  readonly scoreOptions = [1, 2, 3, 4, 5];
  readonly skeletonItems = [1, 2, 3, 4];
  readonly commentPageSize = 5;
  visibleCommentCount = 5;
  readonly commentForm;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly prService: PrService,
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly sanitizer: DomSanitizer,
    private readonly modalService: NgbModal,
    httpBackend: HttpBackend,
  ) {
    this.publicHttp = new HttpClient(httpBackend);
    this.commentForm = this.fb.nonNullable.group({
      score: [5, [Validators.required, Validators.min(1), Validators.max(5)]],
      name: ['', [Validators.required, Validators.maxLength(255)]],
      email: ['', [Validators.required, Validators.email, Validators.maxLength(255)]],
      comment: ['', [Validators.required, Validators.maxLength(2000)]]
    });
  }

  ngOnInit(): void {
    const runpage = this.route.snapshot.paramMap.get('runpage');
    const isPreview = this.route.snapshot.queryParamMap.get('preview') === '1';
    if (isPreview && this.loadPreviewPage()) {
      return;
    }
    if (!runpage) {
      this.notFound = true;
      this.isLoading = false;
      return;
    }
    this.currentRunpage = runpage;
    this.loadPage(runpage);
  }

  onSearch(): void {
    const query = this.searchQuery.trim();
    void this.router.navigate(['/pr'], query ? {queryParams: {q: query}} : {});
  }

  private loadPreviewPage(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const raw = window.localStorage.getItem(PrPublicShowComponent.PREVIEW_STORAGE_KEY);
    if (!raw) {
      this.notFound = true;
      this.isLoading = false;
      return false;
    }
    try {
      this.page = this.normalizePagePaths(JSON.parse(raw) as PrPageDto);
      this.currentRunpage = this.page?.runpage ?? 'preview';
      this.certificationViewMode = this.resolveCertificationViewMode();
      this.expandAllCertifications = false;
      this.expandedCertificationId = null;
      this.visibleCommentCount = this.commentPageSize;
      this.notFound = false;
      this.isLoading = false;
      this.cdr.detectChanges();
      return true;
    } catch {
      this.notFound = true;
      this.isLoading = false;
      return false;
    }
  }

  private loadPage(runpage: string): void {
    this.isLoading = true;
    this.notFound = false;
    this.publicHttp.get<PrPageDto>(`/api/pr/pages/runpage/${encodeURIComponent(runpage)}`).subscribe({
      next: (page) => {
        this.page = this.normalizePagePaths(page);
        this.certificationViewMode = this.resolveCertificationViewMode();
        this.expandAllCertifications = false;
        this.expandedCertificationId = null;
        this.notFound = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.page = undefined;
        this.notFound = true;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private normalizePagePaths(page: PrPageDto): PrPageDto {
    return {
      ...page,
      coverPath: this.normalizeImagePath(page.coverPath ?? null),
      albumPaths: (page.albumPaths ?? []).map(path => this.normalizeImagePath(path)).filter((path): path is string => !!path)
    } as PrPageDto;
  }

  private normalizeImagePath(path: string | null | undefined): string | null {
    const trimmed = `${path ?? ''}`.trim();
    if (!trimmed) {
      return null;
    }
    if (trimmed === '/api/pr/pages/cover-image') {
      return null;
    }
    if (trimmed.startsWith('/api/storage/public/')) {
      return trimmed;
    }
    if (trimmed.startsWith('/images/pr-cover-')) {
      return `/api/storage/public/image/pr-cover/${trimmed.substring('/images/pr-cover-'.length)}`;
    }
    return trimmed;
  }

  private resolveCertificationViewMode(): 'grid' | 'row' {
    if (this.certificationDetails.length <= 1) {
      return 'row';
    }
    return this.isTemplateTwo ? 'row' : 'grid';
  }

  get isHeadless(): boolean {
    return this.route.snapshot.data['headless'] === true;
  }

  get basePath(): string {
    return this.isHeadless ? '/pr-headless' : '/pr';
  }

  get isPreview(): boolean {
    return this.route.snapshot.queryParamMap.get('preview') === '1';
  }

  closePreview(): void {
    if (typeof window !== 'undefined') {
      window.close();
    }
  }

  get businessLabels(): string[] {
    return this.page?.businesses?.map(item => item.label ?? '').filter(Boolean) ?? [];
  }

  get certificationLabels(): string[] {
    return this.page?.certifications?.map(item => item.label ?? '').filter(Boolean) ?? [];
  }

  get certificationDetails(): PrCertificationDetailDto[] {
    return this.page?.certificationDetails ?? [];
  }

  get isTemplateTwo(): boolean {
    return (this.page?.templateName ?? '').trim() === 'Template 1';
  }

  get tags(): string[] {
    return `${this.page?.pageTag ?? ''}`
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  }

  get comments(): PrPageCommentDto[] {
    return this.page?.comments ?? [];
  }

  get visibleComments(): PrPageCommentDto[] {
    return this.comments.slice(0, this.visibleCommentCount);
  }

  get hasMoreComments(): boolean {
    return this.visibleCommentCount < this.comments.length;
  }

  loadMoreComments(): void {
    this.visibleCommentCount += this.commentPageSize;
  }

  get albumPaths(): string[] {
    return this.page?.albumPaths ?? [];
  }

  get averageScore(): number {
    if (this.comments.length === 0) {
      return 0;
    }
    const total = this.comments.reduce((sum, item) => sum + (item.score ?? 0), 0);
    return total / this.comments.length;
  }

  get showComments(): boolean {
    return this.page?.showComment === 1;
  }

  get googleMapUrl(): string | null {
    if (this.page?.hideLatitude !== false || this.page?.hideLongitude !== false) {
      return null;
    }
    const latitude = this.normalizeText(this.page?.latitude);
    const longitude = this.normalizeText(this.page?.longitude);
    if (!latitude || !longitude) {
      return null;
    }
    return `https://www.google.com/maps/place/${encodeURIComponent(latitude)},${encodeURIComponent(longitude)}`;
  }

  get hasContactInfo(): boolean {
    const page = this.page;
    if (!page) {
      return false;
    }
    return [
      page.address,
      page.tel,
      page.email,
      page.latitude,
      page.longitude
    ].some(value => this.normalizeText(value).length > 0);
  }

  get contactInfoItems(): ContactInfoItem[] {
    const page = this.page;
    if (!page) {
      return [];
    }
    return [
      {
        key: 'address',
        label: 'ที่อยู่',
        value: this.resolveContactValue(page.address, page.hideAddress),
        href: null,
        hidden: page.hideAddress !== false,
        icon: 'bi-geo-alt-fill'
      },
      {
        key: 'tel',
        label: 'โทรศัพท์',
        value: this.resolveContactValue(page.tel, page.hideTel),
        href: page.hideTel === false ? this.buildTelHref(page.tel) : null,
        hidden: page.hideTel !== false,
        icon: 'bi-telephone-fill'
      },
      {
        key: 'email',
        label: 'อีเมล',
        value: this.resolveContactValue(page.email, page.hideEmail),
        href: page.hideEmail === false ? this.buildEmailHref(page.email) : null,
        hidden: page.hideEmail !== false,
        icon: 'bi-envelope-fill'
      },
      {
        key: 'latitude',
        label: 'ละติจูด',
        value: this.resolveContactValue(page.latitude, page.hideLatitude),
        href: null,
        hidden: page.hideLatitude !== false,
        icon: 'bi-geo-alt'
      },
      {
        key: 'longitude',
        label: 'ลองจิจูด',
        value: this.resolveContactValue(page.longitude, page.hideLongitude),
        href: null,
        hidden: page.hideLongitude !== false,
        icon: 'bi-geo-alt'
      }
    ];
  }

  get contactTelUrl(): string | null {
    if (this.page?.hideTel !== false) {
      return null;
    }
    return this.buildTelHref(this.page?.tel);
  }

  get contactEmailUrl(): string | null {
    if (this.page?.hideEmail !== false) {
      return null;
    }
    return this.buildEmailHref(this.page?.email);
  }

  get createdDateLabel(): string {
    if (!this.page?.createdDate) {
      return '';
    }
    return this.formatThaiDateTimeLabel(this.page.createdDate, 'วันที่สร้าง');
  }

  get updatedDateLabel(): string {
    if (!this.page?.modifiedDate) {
      return '';
    }
    return this.formatThaiDateTimeLabel(this.page.modifiedDate, 'วันที่อัปเดต');
  }

  private formatThaiDateTimeLabel(value: Date | string, prefix: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const datePart = date.toLocaleDateString('th-TH', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
    const timePart = date.toLocaleTimeString('th-TH', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).replace(':', '.');
    return `${prefix}: ${datePart} เวลา ${timePart} น.`;
  }

  get productDetailHtml(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.renderMarkdown(this.page?.productDetail ?? this.page?.pateType ?? ''));
  }

  private renderMarkdown(markdown: string): string {
    const escaped = markdown
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    const withBlocks = escaped
      .split('\n')
      .map((line) => {
        if (line.startsWith('## ')) {
          return `<h2>${line.slice(3)}</h2>`;
        }
        if (line.startsWith('> ')) {
          return `<blockquote>${line.slice(2)}</blockquote>`;
        }
        if (line.startsWith('- ')) {
          return `<li>${line.slice(2)}</li>`;
        }
        return line ? `<p>${line}</p>` : '';
      })
      .join('');

    return withBlocks
      .replace(/<li>(.*?)<\/li>(<li>.*?<\/li>)+/g, (match) => `<ul>${match}</ul>`)
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\[(.*?)\]\((https?:\/\/.*?)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
  }

  toggleCertification(detail: PrCertificationDetailDto): void {
    if (this.certificationViewMode === 'grid') {
      return;
    }
    if (this.expandAllCertifications) {
      this.expandAllCertifications = false;
    }
    const id = detail.id ?? null;
    this.expandedCertificationId = this.expandedCertificationId === id ? null : id;
  }

  isCertificationExpanded(detail: PrCertificationDetailDto): boolean {
    if (this.certificationViewMode === 'grid') {
      return true;
    }
    if (this.expandAllCertifications) {
      return true;
    }
    return this.expandedCertificationId === (detail.id ?? null);
  }

  setCertificationViewMode(mode: 'grid' | 'row'): void {
    this.certificationViewMode = mode;
    this.expandAllCertifications = false;
    this.expandedCertificationId = null;
  }

  expandAllCertificationDetails(): void {
    if (this.certificationViewMode !== 'row') {
      return;
    }
    this.expandAllCertifications = true;
    this.expandedCertificationId = null;
  }

  collapseAllCertificationDetails(): void {
    this.expandAllCertifications = false;
    this.expandedCertificationId = null;
  }

  openAlbumImage(path: string): void {
    this.selectedAlbumImage = path;
  }

  openContactModal(): void {
    if (!this.hasContactInfo) {
      return;
    }
    this.modalService.open(this.contactModalTpl, {
      centered: true,
      scrollable: true,
      size: 'lg',
      windowClass: 'pr-contact-modal-window'
    });
  }

  closeAlbumImage(): void {
    this.selectedAlbumImage = null;
  }

  scrollToTop(): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.scrollTo({top: 0, behavior: 'smooth'});
  }

  showPreviousAlbumImage(): void {
    if (!this.selectedAlbumImage || this.albumPaths.length <= 1) {
      return;
    }
    const currentIndex = this.albumPaths.indexOf(this.selectedAlbumImage);
    const nextIndex = currentIndex <= 0 ? this.albumPaths.length - 1 : currentIndex - 1;
    this.selectedAlbumImage = this.albumPaths[nextIndex] ?? this.selectedAlbumImage;
  }

  showNextAlbumImage(): void {
    if (!this.selectedAlbumImage || this.albumPaths.length <= 1) {
      return;
    }
    const currentIndex = this.albumPaths.indexOf(this.selectedAlbumImage);
    const nextIndex = currentIndex >= this.albumPaths.length - 1 ? 0 : currentIndex + 1;
    this.selectedAlbumImage = this.albumPaths[nextIndex] ?? this.selectedAlbumImage;
  }

  @HostListener('window:keydown', ['$event'])
  handleAlbumLightboxKeyboard(event: KeyboardEvent): void {
    if (!this.selectedAlbumImage) {
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.showPreviousAlbumImage();
      return;
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.showNextAlbumImage();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeAlbumImage();
    }
  }

  private normalizeText(value: string | null | undefined): string {
    return `${value ?? ''}`.trim();
  }

  private resolveContactValue(value: string | null | undefined, hidden: boolean | null | undefined): string {
    if (hidden !== false) {
      return 'ปกปิดข้อมูล';
    }
    const text = this.normalizeText(value);
    return text || '-';
  }

  private buildTelHref(value: string | null | undefined): string | null {
    const text = this.normalizeText(value);
    if (!text) {
      return null;
    }
    const cleaned = text.replace(/[^\d+]/g, '');
    return cleaned ? `tel:${cleaned}` : null;
  }

  private buildEmailHref(value: string | null | undefined): string | null {
    const text = this.normalizeText(value);
    return text ? `mailto:${text}` : null;
  }

  isFieldInvalid(fieldName: 'score' | 'name' | 'email' | 'comment'): boolean {
    const control = this.commentForm.get(fieldName);
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  getFieldError(fieldName: 'score' | 'name' | 'email' | 'comment'): string {
    const control = this.commentForm.get(fieldName);
    if (!control?.errors || (!control.touched && !control.dirty)) {
      return '';
    }
    if (control.errors['required']) {
      if (fieldName === 'score') {
        return 'กรุณาเลือกระดับความคิดเห็น';
      }
      if (fieldName === 'name') {
        return 'กรุณากรอกชื่อและนามสกุล';
      }
      if (fieldName === 'email') {
        return 'กรุณากรอกอีเมล';
      }
      return 'กรุณากรอกความคิดเห็น';
    }
    if (control.errors['email']) {
      return 'รูปแบบอีเมลไม่ถูกต้อง';
    }
    if (control.errors['maxlength']) {
      if (fieldName === 'comment') {
        return 'ความคิดเห็นต้องไม่เกิน 2000 ตัวอักษร';
      }
      if (fieldName === 'email') {
        return 'อีเมลต้องไม่เกิน 255 ตัวอักษร';
      }
      return 'ชื่อและนามสกุลต้องไม่เกิน 255 ตัวอักษร';
    }
    return '';
  }

  submitComment(): void {
    if (!this.showComments || !this.currentRunpage) {
      return;
    }
    this.commentSaved = false;
    this.submitError = '';
    if (this.commentForm.invalid) {
      this.commentForm.markAllAsTouched();
      return;
    }

    this.isSubmittingComment = true;
    const payload: PrPageCommentCreateDto = {
      score: this.commentForm.getRawValue().score,
      name: this.commentForm.getRawValue().name.trim(),
      email: this.commentForm.getRawValue().email.trim(),
      comment: this.commentForm.getRawValue().comment.trim()
    };

    this.prService.createComment(this.currentRunpage, payload).subscribe({
      next: (comment) => {
        this.page = {
          ...this.page,
          comments: [comment, ...(this.page?.comments ?? [])]
        } as PrPageDto;
        this.commentSaved = true;
        this.commentForm.reset({
          score: 5,
          name: '',
          email: '',
          comment: ''
        });
        this.isSubmittingComment = false;
        this.cdr.detectChanges();
      },
      error: (error: HttpErrorResponse) => {
        this.submitError = error.error?.message || 'ไม่สามารถบันทึกความคิดเห็นได้ กรุณาลองใหม่อีกครั้ง';
        this.isSubmittingComment = false;
        this.cdr.detectChanges();
      }
    });
  }
}
